import React, { type ReactNode } from 'react';

import PropTypes from 'prop-types';

import { IconButton } from '@adsk/alloy-react-button';
import { Container, useForwardedRef } from '@adsk/alloy-react-helpers';
import { XIcon } from '@adsk/alloy-react-icon';
import theme, {
  StylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { getTokenAriaLabel } from '../../helpers';
import { useTranslation } from '../../i18n';

export type TokenProps = Omit<StylableComponent<HTMLDivElement>, 'content'> & {
  content?: ReactNode;
  disabled?: boolean;
  container?: Container;
  onClickX?: () => void;
  renderLeftComponent?: () => ReactNode;
  onRemove?: () => void;
};

const REMOVAL_KEYS = ['Delete', 'Backspace'];
const X_TRIGGER_KEYS = [...REMOVAL_KEYS, ' ', 'Enter'];

const Token = React.forwardRef<HTMLDivElement, TokenProps>(
  (
    {
      className,
      style,
      disabled,
      content,
      container,
      onClickX,
      renderLeftComponent,
      onRemove,
      ...props
    },
    ref,
  ) => {
    const tokenRef = useForwardedRef(ref);
    const { t } = useTranslation();

    const _ariaLabel = props['aria-label'];

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }

        if (REMOVAL_KEYS.includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          onRemove?.();
        }
      },
      [disabled, onRemove],
    );

    const handleXClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onClickX?.();
    };

    const handleXKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (X_TRIGGER_KEYS.includes(e.key)) {
        onClickX?.();
      }
    };

    const interactiveTokenProps = React.useMemo(() => {
      if (disabled) return {};

      const [type, label] = getTokenAriaLabel({
        content,
        'aria-label': _ariaLabel,
      });

      const ariaLabel = type === 'OWN' ? label : t(type, { label });

      return {
        onKeyDown: handleKeyDown,
        tabIndex: 0,
        role: 'button',
        'aria-label': ariaLabel,
      };
    }, [disabled, handleKeyDown, content, _ariaLabel, t]);

    return (
      <div
        ref={tokenRef}
        className={className}
        data-testid={'token'}
        {...interactiveTokenProps}
        css={[
          {
            display: 'flex',
            height: 24,
            alignItems: 'center',
            padding: `${theme.spacing.XXXS}px ${theme.spacing.XS}px`,
            margin: theme.spacing.XXS,
            borderRadius: theme.borderRadius.medium,
            background: theme.tokens.colors.background.tertiary.value,
            color: disabled
              ? theme.tokens.colors.text.inactive.value
              : 'inherit',
            cursor: disabled ? 'not-allowed' : 'inherit',
            userSelect: 'none',
            minWidth: 0,
            '&:focus': {
              outline: `2px solid ${theme.tokens.colors.border.focused.value}`,
              outlineOffset: 1,
            },
          },
          theme.typography.bodyMedium,
          style,
        ]}
        {...props}
      >
        {renderLeftComponent?.()}
        <OverflowTooltip
          singleLine
          autoTipOnEllipsis
          target={tokenRef}
          container={container}
          getTargetProps={() => ({ style: { maxWidth: 172, minWidth: 0 } })}
        >
          {content}
        </OverflowTooltip>
        <IconButton
          renderIcon={XIcon.renderIcon}
          size={16}
          style={{
            marginLeft: theme.spacing.XS,
          }}
          disabled={disabled}
          onMouseDown={handleXClick}
          onKeyDown={handleXKeyDown}
        />
      </div>
    );
  },
);

Token.displayName = 'Token';

Token.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Token content */
  content: PropTypes.any,
  /** If the token is unclickable */
  disabled: PropTypes.bool,
  /** The container */
  container: PropTypes.any,
  /** Function executed upon clicking the X icon */
  onClickX: PropTypes.func,
  /** component to render to the left of the token's content (outside the overflow tooltip) */
  renderLeftComponent: PropTypes.func,
  /** Function executed when Backspace or Delete key is pressed while token is focused */
  onRemove: PropTypes.func,
};

export default Token;
